import { getHeroTemplate, HeroConfig } from './hero';
import {
  CONTACT_COLOR_DEFAULTS,
  normalizeFooterTemplateCode,
  normalizeHeaderTemplateCode,
  resolveHeroSettingsKey,
} from './templateCodes';
import {
  HeaderConfig,
  HeaderMenuItem,
  HeaderSocialLink,
  HeaderVariant,
  SocialNetwork,
} from './header';
import { FooterConfig, FooterLink, FooterLogo, FooterOfficeInfo } from './footer';
import { resolveActiveSearcherId, SearcherItem } from 'shared/searchers';
// Imported from the leaf module, not the feature barrel: the barrel pulls in the
// form components (and the legacy flights chain behind one of them), which must
// not reach SSR-only or test-only consumers of this mapper.
import { REAL_SEARCHER_IDS, hasSearcherForm } from 'frontend/features/searchers/types';
import type { RealSearcherId } from 'frontend/features/searchers/types';
import { buildIdSlugPath } from 'legacyfrontend/utils/slugify';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { resolveSectionIconSrc } from 'frontend/shared/sectionIconCatalog';

const LEGACY_SOCIAL_NETWORK_BY_DESCRIPCION: Record<string, SocialNetwork> = {
  Facebook: 'facebook',
  Instagram: 'instagram',
  Twitter: 'twitter',
  YouTube: 'youtube',
  TikTok: 'tiktok',
  Linkedin: 'linkedin',
};

const ABOUT_US_TIPO_ENLACE_NOMBRE = 'about-us-footer';

export interface LegacySocialNetwork {
  valor: string;
  tipoEnlace?: { descripcion?: string | null } | null;
}

export interface LegacyMainMenuItem {
  id: number | string;
  descripcion?: string | null;
  icono?: { codigo?: string | null; descripcion?: string | null } | null;
  interna?: boolean;
  activo?: boolean;
  tipoSeccion?: { nombre?: string | null } | null;
  contenido?: string | null;
}

export interface LegacyFooterLogo {
  valor?: string | null;
  url?: string | null;
  tipoEnlace?: { descripcion?: string | null; nombre?: string | null } | null;
}

export interface LegacyFooterDetalleItem {
  valor?: string | null;
  categoria?: { nombre?: string | null } | null;
  tipoEnlace?: {
    descripcion?: string | null;
    nombre?: string | null;
    categoria?: { nombre?: string | null } | null;
  } | null;
}

export interface LegacyFooterSource {
  razonSocial?: string | null;
  legajo?: string | null;
  cuit?: string | null;
  /**
   * Partner/institution logos (same source as the legacy LogosFooter) — not
   * something Personalizar's settings blob stores.
   */
  institucionalLogos?: LegacyFooterLogo[];
  /**
   * Raw footer detalle rows. About-us HTML lives on the `about-us-footer`
   * tipoEnlace — not in Personalizar's settings blob.
   */
  detalle?: LegacyFooterDetalleItem[];
}

export interface LegacyFooterLink {
  id?: string | number;
  valor?: string | null;
  categoria?: { nombre?: string | null } | null;
  tipoEnlace?: {
    descripcion?: string | null;
    nombre?: string | null;
    categoria?: { nombre?: string | null } | null;
  } | null;
}

export interface LegacyBrandingSource {
  logoImageUrl: string;
  phone?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  socialNetworks?: LegacySocialNetwork[];
  isPackageSearcherEnabled: boolean;
  isFlightSearcherEnabled: boolean;
  isHotelSearcherEnabled: boolean;
  /**
   * Url-only tabs (autos/actividades/traslados/hoteles-url) resolved from the
   * tenant's acciones — see `useSearcherLinks`. Absent means the agency has none active.
   */
  linkSearchers?: SearcherItem[];
  /**
   * The tenant's real institutional menu (same source as the legacy
   * NavBarMobile), used for the header's menu drawer — not something
   * Personalizar's hero settings configure.
   */
  mainMenu?: LegacyMainMenuItem[];
  /**
   * Institutional registration data, partner logos and about-us HTML — same
   * source as the legacy Footer, not something Personalizar's settings blob stores.
   */
  footer?: LegacyFooterSource;
  /**
   * The tenant's real legal/utility links (same source as the legacy Footer's
   * "Otros Enlaces" modal) — not something Personalizar's settings configure.
   */
  footerOtherLinks?: LegacyFooterLink[];
  paisId?: number;
}

export interface BrandingProps {
  templateCode: string;
  footerTemplateCode: string;
  heroConfig: Partial<HeroConfig>;
  headerConfig: Partial<HeaderConfig>;
  footerConfig: Partial<FooterConfig>;
  footerOfficeInfo: FooterOfficeInfo;
  variant: HeaderVariant;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mapSocialLinks(socialNetworks: LegacySocialNetwork[] | undefined): HeaderSocialLink[] {
  return (socialNetworks || []).reduce<HeaderSocialLink[]>((links, item) => {
    const network = LEGACY_SOCIAL_NETWORK_BY_DESCRIPCION[item.tipoEnlace?.descripcion || ''];
    if (!network || !item.valor) {
      return links;
    }
    return [...links, { network, url: item.valor, show: true }];
  }, []);
}

// Mirrors legacyfrontend/components/commons.js's urlForNavBarItem.
function urlForMainMenuItem(item: LegacyMainMenuItem): string {
  if (item.tipoSeccion?.nombre === 'URL') {
    return item.contenido || '';
  }
  return buildIdSlugPath(ROUTE_KEYS.CUSTOM_SECTION, item.id, item.descripcion || '');
}

// The menu drawer's items come from the tenant's real institutional menu
// (legacyfrontend/components/home/NavBarMobile.js's data source), not from
// Personalizar's hero settings.
function mapMainMenuToHeaderMenuItems(
  mainMenu: LegacyMainMenuItem[] | undefined,
): HeaderMenuItem[] {
  return (mainMenu || [])
    .filter((item) => item?.activo !== false)
    .map((item) => ({
      id: item.id,
      label: item.descripcion || '',
      icon: item.icono?.codigo || undefined,
      iconSrc: resolveSectionIconSrc(item.icono?.codigo, item.icono?.descripcion) || undefined,
      url: urlForMainMenuItem(item),
      target: item.interna ? '_self' : '_blank',
    }));
}

const OTHER_LINK_CATEGORY_NAMES = new Set([
  'enlaces-legales',
  'enlaces-utiles-travelers',
  'links-legales',
  'links-utiles',
]);

function footerDetalleCategoryName(
  item: LegacyFooterLink | LegacyFooterDetalleItem | undefined,
): string {
  return item?.categoria?.nombre || item?.tipoEnlace?.categoria?.nombre || '';
}

function toFooterLink(link: LegacyFooterLink | LegacyFooterDetalleItem): FooterLink {
  return {
    text: link.tipoEnlace?.descripcion || link.tipoEnlace?.nombre || 'Enlace',
    url: link.valor || '',
  };
}

// Mirrors legacyfrontend/components/footer/OtherLinksModal.js's link shape.
// Personalizar writes these rows onto empresa detalle; the preview reads them
// from the Personalizar API, the storefront from initApp footer.detalle.
function mapPublishedFooterOtherLinks(value: unknown): FooterLink[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const record = item as { text?: unknown; url?: unknown };
    const url = typeof record.url === 'string' ? record.url.trim() : '';
    if (!url) {
      return [];
    }
    const text =
      typeof record.text === 'string' && record.text.trim() ? record.text.trim() : 'Enlace';
    return [{ text, url }];
  });
}

function mapFooterOtherLinks(
  links: LegacyFooterLink[] | undefined,
  detalle?: LegacyFooterDetalleItem[],
): FooterLink[] {
  const fromSplit = (links || []).filter((link) => link?.valor).map(toFooterLink);
  if (fromSplit.length > 0) return fromSplit;

  return (detalle || [])
    .filter(
      (item) =>
        Boolean(item?.valor) &&
        item.tipoEnlace?.nombre !== ABOUT_US_TIPO_ENLACE_NOMBRE &&
        OTHER_LINK_CATEGORY_NAMES.has(footerDetalleCategoryName(item)),
    )
    .map(toFooterLink);
}

// Mirrors legacyfrontend/components/footer/LogosFooter.js's detalle shape
// (valor = image, url = optional link, tipoEnlace = name).
function mapFooterLogos(logos: LegacyFooterLogo[] | undefined): FooterLogo[] {
  return (logos || [])
    .filter((logo) => logo?.valor)
    .map((logo) => ({
      name: logo.tipoEnlace?.descripcion || logo.tipoEnlace?.nombre || '',
      imageUrl: logo.valor || '',
      linkUrl: logo.url || null,
    }));
}

// Mirrors legacyfrontend/components/Footer.js's about-us lookup.
function mapAboutUsHtml(detalle: LegacyFooterDetalleItem[] | undefined): string {
  const row = (detalle || []).find(
    (item) => item?.tipoEnlace?.nombre === ABOUT_US_TIPO_ENLACE_NOMBRE,
  );
  return row?.valor || '';
}

function mapFooterOfficeInfo(
  footer: LegacyFooterSource | undefined,
  paisId: number | undefined,
): FooterOfficeInfo {
  return {
    razonSocial: footer?.razonSocial || undefined,
    legajo: footer?.legajo || undefined,
    cuit: footer?.cuit || undefined,
    paisId,
  };
}

// A searcher tab is offered only when this agency has it enabled AND the searchers
// feature actually has a form registered for it.
function enabledRealSearcherIds(legacy: LegacyBrandingSource): RealSearcherId[] {
  const enabled: Partial<Record<RealSearcherId, boolean>> = {
    paquetes: legacy.isPackageSearcherEnabled,
    vuelos: legacy.isFlightSearcherEnabled,
    hoteles: legacy.isHotelSearcherEnabled,
  };
  return REAL_SEARCHER_IDS.filter((id) => enabled[id] && hasSearcherForm(id));
}

/**
 * Resolves the tabs the hero shows: the agency's enabled searchers with a real
 * form, plus the url-only tabs it has active. The order configured in
 * Personalizar wins; anything unavailable is dropped.
 */
function resolveConfiguredSearcherId(
  tab: SearcherItem,
  enabledIds: RealSearcherId[],
  availableById: Map<string, SearcherItem>,
): string {
  if (tab.id !== 'hoteles') {
    return tab.id;
  }

  const bmEnabled = enabledIds.includes('hoteles');
  if ((tab.urlOnly || !bmEnabled) && availableById.has('hoteles-url')) {
    return 'hoteles-url';
  }

  return 'hoteles';
}

function resolveOfferedSearchers(
  configuredTabs: SearcherItem[] | undefined,
  enabledIds: RealSearcherId[],
  linkSearchers: SearcherItem[],
): SearcherItem[] {
  const availableById = new Map<string, SearcherItem>([
    ...enabledIds.map((id): [string, SearcherItem] => [id, { id, show: true }]),
    ...linkSearchers.map((item): [string, SearcherItem] => [item.id, { ...item, show: true }]),
  ]);

  const configuredIds = (configuredTabs || [])
    .filter((tab) => tab?.show !== false)
    .map((tab) => resolveConfiguredSearcherId(tab, enabledIds, availableById));
  // A published searcherTabs array is authoritative — including when every tab
  // is `show: false`. Falling back to every URL searcher would ignore Personalizar.
  const orderedIds = Array.isArray(configuredTabs)
    ? Array.from(new Set(configuredIds))
    : [...enabledIds, ...linkSearchers.map((item) => item.id)];

  return orderedIds
    .map((id) => availableById.get(id))
    .filter((item): item is SearcherItem => Boolean(item));
}

// Builds the props the real storefront needs to render the new Header/Hero, combining the
// agency's persisted `settings` (hero copy/style, published via the intranet's Personalizar
// screen) with data this SSR app already loads for the legacy header (logo, contact info,
// social links, which searchers are enabled). Returns null when the agency hasn't published
// anything yet (`settings` is null/malformed) — callers should fall back to the legacy header.
export function mapSettingsToBrandingProps(
  settings: Record<string, unknown> | null,
  legacy: LegacyBrandingSource,
): BrandingProps | null {
  if (!isRecord(settings)) {
    return null;
  }

  const templateCodeRaw = typeof settings.templateCode === 'string' ? settings.templateCode : null;
  const templateCode = templateCodeRaw ? normalizeHeaderTemplateCode(templateCodeRaw) : null;
  const heroByTemplate = isRecord(settings.hero) ? settings.hero : null;
  const heroKey = templateCode && heroByTemplate ? resolveHeroSettingsKey(templateCode) : null;
  const rawHeroConfig =
    heroKey && heroByTemplate && isRecord(heroByTemplate[heroKey]) ? heroByTemplate[heroKey] : null;
  if (!templateCode || !rawHeroConfig) {
    return null;
  }

  const enabledIds = enabledRealSearcherIds(legacy);
  const configuredTabs = Array.isArray(rawHeroConfig.searcherTabs)
    ? (rawHeroConfig.searcherTabs as SearcherItem[])
    : undefined;
  const searcherTabs = resolveOfferedSearchers(
    configuredTabs,
    enabledIds,
    legacy.linkSearchers || [],
  );
  const activeSearcherId = resolveActiveSearcherId(
    searcherTabs,
    typeof rawHeroConfig.activeSearcherTabId === 'string'
      ? rawHeroConfig.activeSearcherTabId
      : undefined,
  );

  const heroConfig: Partial<HeroConfig> = {
    ...(rawHeroConfig as Partial<HeroConfig>),
    searcherTabs,
    activeSearcherTabId: activeSearcherId,
    // The search module needs at least one searcher with a form; url-only tabs
    // ride along next to it but never keep it open on their own.
    showSearchers:
      (rawHeroConfig.showSearchers as boolean | undefined) !== false && enabledIds.length > 0,
  };

  const rawHeaderSettings = isRecord(settings.header) ? settings.header : {};
  const rawHeaderConfig = rawHeaderSettings as Partial<HeaderConfig>;
  const contactColorsByTemplate = isRecord(rawHeaderSettings.contactColorsByTemplate)
    ? rawHeaderSettings.contactColorsByTemplate
    : {};
  const templateContactColors = isRecord(contactColorsByTemplate[templateCode])
    ? contactColorsByTemplate[templateCode]
    : {};
  const contactDefaults = CONTACT_COLOR_DEFAULTS[templateCode];
  const resolvedContactColor =
    typeof rawHeaderConfig.contactColor === 'string'
      ? rawHeaderConfig.contactColor
      : typeof templateContactColors.contactColor === 'string'
        ? templateContactColors.contactColor
        : contactDefaults.contactColor;
  const resolvedContactTextColor =
    typeof rawHeaderConfig.contactTextColor === 'string'
      ? rawHeaderConfig.contactTextColor
      : typeof templateContactColors.contactTextColor === 'string'
        ? templateContactColors.contactTextColor
        : contactDefaults.contactTextColor;

  const headerConfig: Partial<HeaderConfig> = {
    ...rawHeaderConfig,
    // Color principal lives on the hero blob (and optionally settings.brandColor); the
    // preview sends it on headerConfig too. Project it here so Abrir menú / --brand match.
    brandColor:
      typeof rawHeaderConfig.brandColor === 'string'
        ? rawHeaderConfig.brandColor
        : typeof heroConfig.brandColor === 'string'
          ? heroConfig.brandColor
          : typeof settings.brandColor === 'string'
            ? settings.brandColor
            : undefined,
    brandColor2:
      typeof rawHeaderConfig.brandColor2 === 'string'
        ? rawHeaderConfig.brandColor2
        : typeof heroConfig.brandColor2 === 'string'
          ? heroConfig.brandColor2
          : undefined,
    contactColor: resolvedContactColor,
    contactTextColor: resolvedContactTextColor,
    logoImageUrl: legacy.logoImageUrl,
    phone: legacy.phone || '',
    showPhone: rawHeaderConfig.showPhone !== false && Boolean(legacy.phone),
    email: legacy.email || '',
    showEmail: rawHeaderConfig.showEmail !== false && Boolean(legacy.email),
    whatsapp: legacy.whatsapp || '',
    showWhatsapp: rawHeaderConfig.showWhatsapp !== false && Boolean(legacy.whatsapp),
    socialLinks: mapSocialLinks(legacy.socialNetworks),
    searcherItems: searcherTabs,
    activeSearcherId,
    menuItems: mapMainMenuToHeaderMenuItems(legacy.mainMenu),
  };

  const rawFooterConfig = isRecord(settings.footer)
    ? (settings.footer as Partial<FooterConfig>)
    : {};
  const footerTemplateCode = normalizeFooterTemplateCode(
    typeof rawFooterConfig.templateCode === 'string' ? rawFooterConfig.templateCode : null,
  );
  const footerConfig: Partial<FooterConfig> = {
    ...rawFooterConfig,
    templateCode: footerTemplateCode,
    aboutUsHtml: mapAboutUsHtml(legacy.footer?.detalle),
    otherLinks:
      mapPublishedFooterOtherLinks(rawFooterConfig.otherLinks) ??
      mapFooterOtherLinks(legacy.footerOtherLinks, legacy.footer?.detalle),
    footerLogos: mapFooterLogos(legacy.footer?.institucionalLogos),
    agencyLogoUrl: legacy.logoImageUrl,
  };
  const footerOfficeInfo = mapFooterOfficeInfo(legacy.footer, legacy.paisId);

  const { headerVariant: variant } = getHeroTemplate(templateCode);

  return {
    templateCode,
    footerTemplateCode,
    heroConfig,
    headerConfig,
    footerConfig,
    footerOfficeInfo,
    variant,
  };
}
